import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { useUser } from 'global/UserContext';
import { apiStrapi } from 'services/apiService';
import { Menu, MenuItem, MenuResponse } from 'types/menuTypes';
import { SideMenuConfig } from 'types/userPlanFeaturesTypes';
import { getStoredHeaders } from 'utils/headersManager';
import { logger } from 'utils/logger';
import { isValidMenuRoute } from 'utils/menuUtils';

interface MenuContextProps {
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
  activeSubmenu: string;
  setActiveSubmenu: (submenu: string) => void;
  menu: Menu;
  isLoading: boolean;
  error: string | null;
  clearMenu: () => void;
  refreshMenu: () => Promise<void>;
  sideMenu?: SideMenuConfig | null;
}

const MenuContext = createContext<MenuContextProps | undefined>(undefined);

const STORAGE_KEY = 'menuData';
const STORAGE_KEY_SIDE_MENU = 'sideMenuData';

/**
 * Safely writes to localStorage, catching quota errors.
 */
const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    logger.warn('[MenuContext] Failed to save to localStorage:', err);
  }
};

/**
 * Recursively trims whitespace from all `path` fields in the menu tree.
 * Defends against Strapi data containing trailing/leading spaces in paths.
 */
const normalizeMenuPaths = (menuItems: Menu): Menu => {
  return menuItems.map(item => ({
    ...item,
    path: item.path.trim(),
    ...(item.subMenu ? { subMenu: normalizeMenuPaths(item.subMenu) } : {}),
  }));
};

/**
 * Finds the first valid menu item (with active feature) in the menu tree.
 */
const findFirstValidMenu = (items: MenuItem[]): MenuItem | null => {
  for (const item of items) {
    if (item.feature?.is_active) {
      return item;
    }
    if (item.subMenu) {
      const found = findFirstValidMenu(item.subMenu);
      if (found) return found;
    }
  }
  return null;
};

export const MenuProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, user } = useUser();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string>('');
  const [activeSubmenu, setActiveSubmenu] = useState<string>('');
  const [menu, setMenu] = useState<Menu>([]);
  const [sideMenu, setSideMenu] = useState<SideMenuConfig>({
    contact_info: false,
    coins: false,
    timer: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs to avoid stale closures in useCallback
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Update the menu only if it's different (functional setState to avoid stale closure)
  const updateMenu = useCallback((newMenu: Menu) => {
    const normalized = normalizeMenuPaths(newMenu);
    setMenu(prev => {
      if (JSON.stringify(prev) === JSON.stringify(normalized)) {
        logger.debug('[MenuContext] Received menu is the same as current, not updating.');
        return prev;
      }
      safeLocalStorageSet(STORAGE_KEY, JSON.stringify(normalized));
      logger.debug('[MenuContext] Menu updated and saved to localStorage.');
      return normalized;
    });
  }, []);

  const updateSideMenu = useCallback((newSideMenu: SideMenuConfig) => {
    setSideMenu(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newSideMenu)) {
        logger.debug('[MenuContext] Received SideMenu is the same as current, not updating.');
        return prev;
      }
      safeLocalStorageSet(STORAGE_KEY_SIDE_MENU, JSON.stringify(newSideMenu));
      logger.debug('[MenuContext] SideMenu updated and saved to localStorage.');
      return newSideMenu;
    });
  }, []);

  const clearMenu = useCallback(() => {
    logger.debug('[MenuContext] clearMenu called!');
    setMenu([]);
    setActiveMenu('');
    setActiveSubmenu('');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_SIDE_MENU);
  }, []);

  const hasRequiredHeaders = () => {
    const headers = getStoredHeaders();
    const hasRequired = headers.organizationUUID && headers.cognitoUserPool;
    logger.debug('[MenuContext] Available headers:', {
      hasOrganizationUUID: !!headers.organizationUUID,
      hasCognitoUserPool: !!headers.cognitoUserPool,
    });
    return hasRequired;
  };

  /**
   * Handles post-refresh navigation: checks current route and redirects if needed.
   */
  const handlePostRefreshNavigation = useCallback((enrichedMenu: Menu) => {
    const currentPath = window.location.pathname;
    const isPublicRoute = [
      '/login',
      '/register',
      '/forgot-password',
      '/register-iframe',
    ].includes(currentPath);

    // Exclude event-invite paths and embedded form paths from automatic redirection
    const isEventInvitePath = currentPath.startsWith('/invite/event/');
    const isEmbeddedFormPath = currentPath.startsWith('/form/');

    // Check if the current route is valid based on the menu
    const isCurrentRouteValid = isValidMenuRoute(enrichedMenu, currentPath);

    // Don't auto-navigate from root path - let RootRedirect component handle it
    // Only auto-navigate from public routes or invalid routes (except event-invite and embedded forms)
    if (
      !isEventInvitePath &&
      !isEmbeddedFormPath &&
      currentPath !== '/' &&
      (isPublicRoute || !isCurrentRouteValid)
    ) {
      const firstValidMenu = findFirstValidMenu(enrichedMenu);
      if (firstValidMenu) {
        logger.debug(
          '[MenuContext] Navigating to first valid menu:',
          firstValidMenu.path
        );
        navigateRef.current(firstValidMenu.path, { replace: true });

        // Force menu selection by documentId after navigation
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          if (firstValidMenu.feature && firstValidMenu.feature.documentId) {
            const event = new CustomEvent('forceMenuSelection', {
              detail: firstValidMenu.feature.documentId,
            });
            window.dispatchEvent(event);
          }
        }, 100);
      }
    } else {
      logger.debug('[MenuContext] Keeping current valid route:', { data: currentPath });
    }
  }, []);

  const refreshMenu = useCallback(
    async (shouldNavigate: boolean = false) => {
      // Guard: Use localStorage as fallback to avoid stale closure issues.
      // When called from event handlers, React state (token/user) may not
      // have re-rendered yet, but localStorage is already updated.
      const effectiveToken = token || localStorage.getItem('AUTH_TOKEN');
      if (!effectiveToken) {
        logger.debug('[MenuContext] No token, skipping refreshMenu');
        return;
      }

      const effectiveUserId = user?.id || localStorage.getItem('user_id');
      if (!effectiveUserId) {
        logger.debug('[MenuContext] No user.id, skipping refreshMenu');
        return;
      }

      if (!hasRequiredHeaders()) {
        logger.debug(
          '[MenuContext] Required headers not found, waiting for initialization...'
        );
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        logger.debug('[MenuContext] Starting refreshMenu');

        const response = await apiStrapi.get<MenuResponse>('/user-person/menu');
        const menuData = response.data.modules;
        const sideMenuData = response.data.sideMenu;
        logger.debug('[MenuContext] Menu received from API:', { menuData, sideMenuData });

        if (Array.isArray(menuData) && menuData.length > 0) {
          updateMenu(menuData);
          updateSideMenu(sideMenuData);

          // If should navigate, check current route and redirect if needed
          if (shouldNavigate) {
            handlePostRefreshNavigation(menuData);
          }
        } else {
          logger.error('[MenuContext] Invalid menu format:', menuData);
          throw new Error('Invalid menu format');
        }
      } catch (error: any) {
        logger.error('[MenuContext] Error updating menu:', error);
        if (error.response?.status === 401) {
          logger.debug('[MenuContext] 401 error - Invalid headers or token');
          clearMenu();
        }
        setError('Error loading menu');
      } finally {
        setIsLoading(false);
      }
    },
    [token, user?.id, updateMenu, updateSideMenu, clearMenu, handlePostRefreshNavigation]
  );

  // Load menu from localStorage when the component mounts
  useEffect(() => {
    // Get the token from localStorage as fallback
    const effectiveToken = token || localStorage.getItem('AUTH_TOKEN');
    if (!effectiveToken) {
      // Don't clear the menu automatically! Just log.
      logger.debug('[MenuContext] Token absent, but menu will NOT be cleared automatically.');
      setIsLoading(false);
      return;
    }

    // Guard: Check user.id before proceeding with API calls
    if (!user?.id) {
      logger.debug('[MenuContext] User ID absent, waiting for full authentication.');
      setIsLoading(false);
      return;
    }

    if (!hasRequiredHeaders()) {
      logger.debug('[MenuContext] Waiting for required headers, not clearing menu.');
      setIsLoading(false);
      return;
    }

    // Only load the menu if it hasn't been loaded yet
    if (menu.length === 0) {
      logger.debug('[MenuContext] Menu empty, trying to load from localStorage');
      const storedMenu = localStorage.getItem(STORAGE_KEY);
      const storedSideMenu = localStorage.getItem(STORAGE_KEY_SIDE_MENU);

      if (storedMenu) {
        try {
          const parsedMenu = JSON.parse(storedMenu);
          if (Array.isArray(parsedMenu) && parsedMenu.length > 0) {
            updateMenu(parsedMenu);

            // Also load sideMenu if available
            if (storedSideMenu) {
              try {
                const parsedSideMenu = JSON.parse(storedSideMenu);
                updateSideMenu(parsedSideMenu);
              } catch (error) {
                logger.error('[MenuContext] Error parsing sideMenu from localStorage:', error);
              }
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          logger.error('[MenuContext] Error parsing menu from localStorage:', error);
          clearMenu();
        }
      }
      // If not in localStorage, fetch from API (only if fully authenticated)
      // Use shouldNavigate=true when on a public route (e.g. /login) so we
      // redirect to the first valid menu after loading
      if (token && user?.id && hasRequiredHeaders()) {
        const isOnPublicRoute = ['/login', '/register', '/forgot-password', '/register-iframe']
          .includes(window.location.pathname);
        logger.debug('[MenuContext] Fetching menu from API', { shouldNavigate: isOnPublicRoute });
        refreshMenu(isOnPublicRoute).finally(() => setIsLoading(false));
      } else {
        logger.debug('[MenuContext] Waiting for full authentication to fetch menu from API');
        setIsLoading(false);
      }
    } else {
      logger.debug('[MenuContext] Menu already loaded.');
      setIsLoading(false);
    }
  }, [token, user?.id, menu.length, updateMenu, updateSideMenu, clearMenu, refreshMenu]);

  // Clear the menu when the user logs out
  useEffect(() => {
    if (!token) {
      clearMenu();
      logger.debug('[MenuContext] Menu cleared due to missing token');
    }
    logger.debug('[MenuContext] token changed:', { data: token });
  }, [token, clearMenu]);

  const value = useMemo(
    () => ({
      activeMenu,
      setActiveMenu,
      activeSubmenu,
      setActiveSubmenu,
      menu,
      isLoading,
      error,
      clearMenu,
      refreshMenu,
      sideMenu,
    }),
    [activeMenu, activeSubmenu, menu, isLoading, error, sideMenu, clearMenu, refreshMenu]
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
